import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram, FaAngleUp, FaPhoneSquareAlt } from 'react-icons/fa';
import { useState, useEffect } from 'react';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Show button when page is scrolled up to a certain distance
  const toggleVisibility = () => {
    if (window.pageYOffset > 600) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  // Set the top coordinate to 0
  // make scrolling smooth
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      {/* Modern background pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}></div>
      </div>

      {/* Glowing orbs for modern effect */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-15 animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-15 animate-pulse delay-1000"></div>

      {/* Content container with relative positioning */}
      <div className="relative container mx-auto px-6 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4 backdrop-blur-sm">
            <Link to="/" className="inline-block">
              <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                Hither Tech
              </h3>
            </Link>
            <p className="text-gray-300">Your Trusted Enterprise Solutions Provider in Sri Lanka and UK</p>
          </div>

          {/* Quick Links */}
          <div className="backdrop-blur-sm">
            <h4 className="text-lg font-semibold mb-4 text-gray-100">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/quote" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  Quotations
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="backdrop-blur-sm">
            <h4 className="text-lg font-semibold mb-4 text-gray-100">Services</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/services/internet-services" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  Internet Services
                </Link>
              </li>
              <li>
                <Link to="/services/software-solutions" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  Software Solutions
                </Link>
              </li>
              <li>
                <Link to="/services/mobile-app-development" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  Mobile Development
                </Link>
              </li>
              <li>
                <Link to="/services/security-solutions" className="text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-300 inline-block">
                  Security Solutions
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="backdrop-blur-sm">
            <h4 className="text-lg font-semibold mb-4 text-gray-100">Contact Us</h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-center group">
                <svg className="w-5 h-5 mr-2 text-blue-400 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <a href="mailto:info@hithertech.co.uk" className="group-hover:text-white transition-colors">
                  info@hithertech.co.uk
                </a>
              </li>
              <li className="flex items-center group">
                <FaPhoneSquareAlt className="w-5 h-5 mr-2 text-blue-400 group-hover:scale-110 transition-transform" />
                <a href="tel:+442039165115" className="group-hover:text-white transition-colors">
                  +44 203 916 5115 (Hotline)
                </a>
              </li>
            </ul>
            {/* Social Media Icons */}
            <div className="mt-8 flex space-x-4">
              <a href="https://www.facebook.com/HitherTech" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors transform hover:scale-110">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-blue-600 transition-colors">
                  <FaFacebookF className="w-5 h-5" />
                </div>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors transform hover:scale-110">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-blue-400 transition-colors">
                  <FaTwitter className="w-5 h-5" />
                </div>
              </a>
              <a href="https://www.linkedin.com/company/hither-tech/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors transform hover:scale-110">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-blue-700 transition-colors">
                  <FaLinkedinIn className="w-5 h-5" />
                </div>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors transform hover:scale-110">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-pink-500 transition-colors">
                  <FaInstagram className="w-5 h-5" />
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p>
            &copy; {new Date().getFullYear()}{" "}
            <Link to="https://hithertech.co.uk" target="_blank" title="Hither Tech">
              Hither Tech Ltd.
            </Link>{" "}
            All rights reserved | Registered In England & Wales:{" "}
            <Link
              to="https://find-and-update.company-information.service.gov.uk/company/15846433"
              target="_blank"
              title="HITHER TECH LTD - Companies House"
            >
              15846433
            </Link>
          </p>
          {/* <div className="flex flex-wrap justify-center gap-4 mt-2">
            <Link to="/privacy-policy" className="hover:text-white underline transition-colors">
              Privacy Policy
            </Link>
            <span>|</span>
            <Link to="/terms-of-service" className="hover:text-white underline transition-colors">
              Terms of Service
            </Link>
            <span>|</span>
            <Link to="/sitemap" className="hover:text-white underline transition-colors">
              Sitemap
            </Link>
          </div> */}
        </div>
      </div>

      {/* Scroll to Top Button */}
      {isVisible && (
        <div className="fixed bottom-20 right-6 z-40 p-1">
          <button
            onClick={scrollToTop}
            className="p-2 rounded-full bg-blue-500/5 border border-blue-500 text-blue-500 shadow-lg hover:bg-purple-800/90 transition-colors"
          >
            <FaAngleUp className="w-5 h-5" />
          </button>
        </div>
      )}
    </footer>
  );
};

export default Footer;